import { useState } from 'react';
import {
  Card,
  Button,
  Field,
  Input,
  TabList,
  Tab,
  Spinner,
  Switch,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, Edit20Regular, ShieldErrorRegular } from '@fluentui/react-icons';
import {
  AppConfig, HazardCategory, PpeItem, RegCheck, RiskDefault, AdminConfigKind,
} from '../types';
import { adminUpsert, adminDelete } from '../services/api';

interface Props {
  isAdmin: boolean;
  config: AppConfig;
  onConfigChanged: () => Promise<void>;
}

const useStyles = makeStyles({
  body: { flex: 1, overflowY: 'auto', paddingTop: '14px', paddingBottom: '14px', paddingLeft: '14px', paddingRight: '14px' },
  card: { paddingTop: '14px', paddingBottom: '14px', paddingLeft: '14px', paddingRight: '14px', marginBottom: '12px' },
  tabBar: { marginBottom: '14px' },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '4px',
    paddingRight: '4px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    gap: '8px',
    minHeight: '52px',
    fontSize: '13.5px',
  },
  itemMain: { flex: 1, minWidth: 0 },
  itemLabel: { fontWeight: 500 },
  itemMeta: {
    fontSize: '11.5px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  rowActions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  iconBtn: {
    minWidth: '40px',
    minHeight: '40px',
  },
  addBtn: { width: '100%', marginTop: '12px', minHeight: '48px' },
  forbiddenWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '40px',
    paddingBottom: '40px',
    paddingLeft: '20px',
    paddingRight: '20px',
    textAlign: 'center',
  },
  forbiddenIcon: {
    fontSize: '48px',
    color: '#E3272D',
    marginBottom: '14px',
  },
  forbiddenTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#231F20',
    marginBottom: '8px',
  },
  forbiddenBody: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.5,
    maxWidth: '320px',
  },
  fieldGrid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '10px',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '6px',
    paddingBottom: '6px',
    fontSize: '13.5px',
  },
});

type TabKey = AdminConfigKind;

export function AdminScreen({ isAdmin, config, onConfigChanged }: Props) {
  const styles = useStyles();
  const [tab, setTab] = useState<TabKey>('hazards');
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [deleting, setDeleting] = useState<{ kind: TabKey; id: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fail-closed: if a non-admin somehow lands here, render an Access Denied
  // panel and call NO API. The server also enforces this.
  if (!isAdmin) {
    return (
      <div className={styles.body} data-screen-body>
        <div className={styles.forbiddenWrap}>
          <ShieldErrorRegular className={styles.forbiddenIcon} />
          <div className={styles.forbiddenTitle}>Access denied</div>
          <div className={styles.forbiddenBody}>
            This page is restricted to safety administrators. If you believe this is an error,
            contact James Murphy or Stefan Measey.
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async (kind: TabKey, item: any) => {
    setBusy(true);
    setError(null);
    try {
      await adminUpsert(kind, item);
      await onConfigChanged();
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setError(null);
    try {
      await adminDelete(deleting.kind, deleting.id);
      await onConfigChanged();
      setDeleting(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.body}>
      <TabList
        selectedValue={tab}
        onTabSelect={(_, data) => setTab(data.value as TabKey)}
        className={styles.tabBar}
        size="medium"
      >
        <Tab value="hazards">Hazards</Tab>
        <Tab value="ppe">PPE</Tab>
        <Tab value="regchecks">Reg Checks</Tab>
        <Tab value="risks">Risk Defaults</Tab>
      </TabList>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Operation failed</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {busy && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
          <Spinner size="extra-small" label="Saving…" />
        </div>
      )}

      {tab === 'hazards' && (
        <Card className={styles.card}>
          {config.hazards.map(h => (
            <div key={h.id} className={styles.itemRow}>
              <div className={styles.itemMain}>
                <div className={styles.itemLabel}>{h.label}</div>
                <div className={styles.itemMeta}>
                  id: {h.id}{h.highRisk ? ' · high risk' : ''}{h.custom ? ' · custom-input' : ''}
                </div>
              </div>
              <div className={styles.rowActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setEditing({ kind: 'hazards', item: { ...h } })}
                  aria-label="Edit"
                />
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setDeleting({ kind: 'hazards', id: h.id, label: h.label })}
                  aria-label="Delete"
                />
              </div>
            </div>
          ))}
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            className={styles.addBtn}
            onClick={() => setEditing({
              kind: 'hazards',
              item: { id: '', label: '', highRisk: false, sortOrder: 500 },
            })}
          >
            Add hazard category
          </Button>
        </Card>
      )}

      {tab === 'ppe' && (
        <Card className={styles.card}>
          {config.ppe.map(p => (
            <div key={p.id} className={styles.itemRow}>
              <div className={styles.itemMain}>
                <div className={styles.itemLabel}>{p.label}</div>
                <div className={styles.itemMeta}>id: {p.id}</div>
              </div>
              <div className={styles.rowActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setEditing({ kind: 'ppe', item: { ...p } })}
                  aria-label="Edit"
                />
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setDeleting({ kind: 'ppe', id: p.id, label: p.label })}
                  aria-label="Delete"
                />
              </div>
            </div>
          ))}
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            className={styles.addBtn}
            onClick={() => setEditing({
              kind: 'ppe',
              item: { id: '', label: '', sortOrder: 500 },
            })}
          >
            Add PPE item
          </Button>
        </Card>
      )}

      {tab === 'regchecks' && (
        <Card className={styles.card}>
          {config.regChecks.map(c => (
            <div key={c.id} className={styles.itemRow}>
              <div className={styles.itemMain}>
                <div className={styles.itemLabel}>{c.label}</div>
                <div className={styles.itemMeta}>
                  id: {c.id}{c.required ? ' · required' : ''}{c.hasNA ? ' · N/A allowed' : ''}
                </div>
              </div>
              <div className={styles.rowActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setEditing({ kind: 'regchecks', item: { ...c } })}
                  aria-label="Edit"
                />
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setDeleting({ kind: 'regchecks', id: c.id, label: c.label })}
                  aria-label="Delete"
                />
              </div>
            </div>
          ))}
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            className={styles.addBtn}
            onClick={() => setEditing({
              kind: 'regchecks',
              item: { id: '', label: '', required: true, hasNA: true, sortOrder: 500 },
            })}
          >
            Add regulatory check
          </Button>
        </Card>
      )}

      {tab === 'risks' && (
        <Card className={styles.card}>
          {config.riskDefaults.map(r => (
            <div key={r.id} className={styles.itemRow}>
              <div className={styles.itemMain}>
                <div className={styles.itemLabel}>{r.hazard}</div>
                <div className={styles.itemMeta}>
                  L={r.likelihood} · C={r.consequence} · {r.control || 'no default control'}
                </div>
              </div>
              <div className={styles.rowActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setEditing({ kind: 'risks', item: { ...r } })}
                  aria-label="Edit"
                />
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  className={styles.iconBtn}
                  onClick={() => setDeleting({ kind: 'risks', id: r.id, label: r.hazard })}
                  aria-label="Delete"
                />
              </div>
            </div>
          ))}
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            className={styles.addBtn}
            onClick={() => setEditing({
              kind: 'risks',
              item: { id: '', hazard: '', likelihood: 3, consequence: 3, control: '', sortOrder: 500 },
            })}
          >
            Add risk default
          </Button>
        </Card>
      )}

      {editing && (
        <EditDialog
          state={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          busy={busy}
        />
      )}

      <Dialog open={!!deleting} onOpenChange={(_, d) => { if (!d.open) setDeleting(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete this entry?</DialogTitle>
            <DialogContent>
              <p>"{deleting?.label}" will be removed for all users on the next config refresh (within 5 minutes).</p>
              <p>This action cannot be undone, but you can recreate the entry with the same id afterwards.</p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button appearance="primary" onClick={handleDelete} disabled={busy}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

interface EditingState {
  kind: TabKey;
  item: HazardCategory | PpeItem | RegCheck | RiskDefault;
}

interface EditDialogProps {
  state: EditingState;
  onClose: () => void;
  onSave: (kind: TabKey, item: any) => void;
  busy: boolean;
}

function EditDialog({ state, onClose, onSave, busy }: EditDialogProps) {
  const styles = useStyles();
  const [item, setItem] = useState<any>(state.item);
  const isNew = !state.item.id;

  const update = (patch: Record<string, any>) => setItem((i: any) => ({ ...i, ...patch }));

  const handleSave = () => {
    // Sanitise id: lowercase, replace spaces with dashes if user just typed words
    const cleanId = (item.id || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, '');
    onSave(state.kind, { ...item, id: cleanId });
  };

  return (
    <Dialog open onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {isNew ? 'Add' : 'Edit'} {state.kind === 'hazards' ? 'hazard'
              : state.kind === 'ppe' ? 'PPE item'
              : state.kind === 'regchecks' ? 'regulatory check' : 'risk default'}
          </DialogTitle>
          <DialogContent>
            {(state.kind === 'hazards' || state.kind === 'ppe' || state.kind === 'regchecks') && (
              <div className={styles.fieldGrid2}>
                <Field label="ID (lowercase, no spaces)">
                  <Input
                    value={item.id}
                    size="large"
                    disabled={!isNew}
                    onChange={(_, d) => update({ id: d.value })}
                    placeholder="e.g. asbestos"
                    input={{ style: { fontSize: '16px' } }}
                  />
                </Field>
                <Field label="Sort order">
                  <Input
                    type="number"
                    size="large"
                    value={String(item.sortOrder ?? 500)}
                    onChange={(_, d) => update({ sortOrder: parseInt(d.value) || 500 })}
                    input={{ style: { fontSize: '16px' } }}
                  />
                </Field>
              </div>
            )}

            {state.kind === 'risks' && (
              <Field label="ID (lowercase, no spaces)" style={{ marginBottom: 10 }}>
                <Input
                  value={item.id}
                  size="large"
                  disabled={!isNew}
                  onChange={(_, d) => update({ id: d.value })}
                  placeholder="e.g. ladders"
                  input={{ style: { fontSize: '16px' } }}
                />
              </Field>
            )}

            {(state.kind === 'hazards' || state.kind === 'ppe' || state.kind === 'regchecks') && (
              <Field label={state.kind === 'regchecks' ? 'Question (as shown to worker)' : 'Label'} style={{ marginBottom: 10 }}>
                <Input
                  value={item.label}
                  size="large"
                  onChange={(_, d) => update({ label: d.value })}
                  placeholder={state.kind === 'regchecks'
                    ? 'e.g. Hot work permit signed?'
                    : state.kind === 'ppe'
                      ? 'e.g. Cut-resistant gloves'
                      : 'e.g. Asbestos risk'}
                  input={{ style: { fontSize: '16px' } }}
                />
              </Field>
            )}

            {state.kind === 'risks' && (
              <>
                <Field label="Hazard description (default seed)" style={{ marginBottom: 10 }}>
                  <Input
                    value={item.hazard}
                    size="large"
                    onChange={(_, d) => update({ hazard: d.value })}
                    placeholder="e.g. Working from ladder"
                    input={{ style: { fontSize: '16px' } }}
                  />
                </Field>
                <div className={styles.fieldGrid2}>
                  <Field label="Likelihood (1-5)">
                    <Input
                      type="number"
                      size="large"
                      value={String(item.likelihood)}
                      onChange={(_, d) => update({ likelihood: Math.max(1, Math.min(5, parseInt(d.value) || 3)) })}
                      input={{ style: { fontSize: '16px' } }}
                    />
                  </Field>
                  <Field label="Consequence (1-5)">
                    <Input
                      type="number"
                      size="large"
                      value={String(item.consequence)}
                      onChange={(_, d) => update({ consequence: Math.max(1, Math.min(5, parseInt(d.value) || 3)) })}
                      input={{ style: { fontSize: '16px' } }}
                    />
                  </Field>
                </div>
                <Field label="Default control (optional)" style={{ marginBottom: 10 }}>
                  <Input
                    value={item.control}
                    size="large"
                    onChange={(_, d) => update({ control: d.value })}
                    placeholder="e.g. Three points of contact"
                    input={{ style: { fontSize: '16px' } }}
                  />
                </Field>
                <Field label="Sort order">
                  <Input
                    type="number"
                    size="large"
                    value={String(item.sortOrder ?? 500)}
                    onChange={(_, d) => update({ sortOrder: parseInt(d.value) || 500 })}
                    input={{ style: { fontSize: '16px' } }}
                  />
                </Field>
              </>
            )}

            {state.kind === 'hazards' && (
              <div className={styles.toggleRow}>
                <span>Mark as high-risk (requires extra controls)</span>
                <Switch
                  checked={item.highRisk}
                  onChange={(_, d) => update({ highRisk: d.checked })}
                />
              </div>
            )}

            {state.kind === 'regchecks' && (
              <>
                <div className={styles.toggleRow}>
                  <span>Required to pass (worker can't proceed if "No")</span>
                  <Switch
                    checked={item.required}
                    onChange={(_, d) => update({ required: d.checked })}
                  />
                </div>
                <div className={styles.toggleRow}>
                  <span>Allow "N/A" answer</span>
                  <Switch
                    checked={item.hasNA}
                    onChange={(_, d) => update({ hasNA: d.checked })}
                  />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" onClick={handleSave} disabled={busy}>
              {isNew ? 'Add' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
